import { type JSX, type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import type { IncidentItem } from '../../../src/api/queries';
import type { FacetCount } from '../../../src/api/traffic-queries';
import { UNSPECIFIED_COUNTY } from '../../../src/wire';
import { formatCount, formatDayMonth, formatEuros, formatRate } from '../format';
import {
  categoryLabel,
  countyLabel,
  HOUR_PEAK_LABEL,
  LEADERBOARD_MODE_LABELS,
  LEADERBOARD_NOTHING_READ,
  MEDIAN_DENOMINATOR_NOTE,
  RECORD_DAMAGE_FRAMING,
  subcategoryLabel,
  UNSPECIFIED_COUNTY_HINT,
} from '../labels';
import { POPULATION_URL, POPULATION_VINTAGE } from '../population';
import { type LeaderboardMode, leaderboardRows, sparkbars } from '../tabloo';
import { InfoPopover } from './InfoPopover';
import { Skeleton } from './ui/skeleton';

/**
 * The Tabloo grid (ux-spec §2.1 block 3): four small modules, each one fact with its denominator.
 * Every visual here is CSS or inline SVG — the landing page pulls no charting library, which is the
 * bundle split `App.tsx` was built around.
 */

const RANKED_ROWS = 5;
const RANKS = Array.from({ length: RANKED_ROWS }, (_, i) => i + 1);

/** The ranked list's shape before the facets land, so the card is already its settled height. */
function RankedSkeleton(): JSX.Element {
  return (
    <ol className="mt-3 flex list-none flex-col gap-2 p-0" aria-hidden="true">
      {RANKS.map((rank) => (
        <li key={rank} className="flex flex-col gap-1">
          <Skeleton className="h-4" />
          <Skeleton className="h-1.5" />
        </li>
      ))}
    </ol>
  );
}

export function TablooCard({
  title,
  caption,
  captionClassName = '',
  action,
  children,
}: {
  title: ReactNode;
  caption: string;
  /** Extra classes for the caption line, for a card whose caption gains words with its data. */
  captionClassName?: string;
  action?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-md border border-line bg-surface p-4 shadow-card sm:px-5 sm:py-[1.1rem]">
      <h3 className="flex items-center gap-1.5 font-display text-h3 font-bold tracking-[-0.01em]">
        {title}
      </h3>
      <p className={`mt-0.5 text-caption text-ink-muted ${captionClassName}`}>{caption}</p>
      {children}
      {action}
    </section>
  );
}

/** Maakondade tabloo. The toggle ships with the board: raw counts crown Harjumaa and per-capita
 * flips it, so offering one scale alone would be choosing the answer for the reader. */
export function LeaderboardList({
  counties,
  stated,
  total,
  pending = false,
}: {
  counties: readonly FacetCount[];
  stated: number;
  total: number;
  /** Still fetching, as opposed to answered-with-nothing: one holds space, one prints a dash. */
  pending?: boolean;
}): JSX.Element {
  const [mode, setMode] = useState<LeaderboardMode>('total');
  const rows = leaderboardRows(counties, mode, RANKED_ROWS);

  return (
    <TablooCard
      title={
        <>
          Maakondade tabloo
          <span className="ml-auto inline-flex gap-1">
            {(['total', 'perCapita'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
                className={`rounded-full border px-2 py-0.5 text-[0.68rem] font-semibold ${
                  mode === value
                    ? 'border-accent bg-accent text-surface'
                    : 'border-line bg-surface text-ink-muted'
                }`}
              >
                {LEADERBOARD_MODE_LABELS[value]}
              </button>
            ))}
          </span>
        </>
      }
      caption="Liiklusõnnetusi maakonniti, viimased 30 päeva"
      action={
        <p className="mt-3 text-caption text-ink-muted">
          {total === 0
            ? LEADERBOARD_NOTHING_READ
            : `Maakond on teada ${formatCount(stated)} õnnetusel ${formatCount(total)}-st.`}
          <InfoPopover label="Mille järgi jagatakse">
            {UNSPECIFIED_COUNTY_HINT} Elaniku kohta arvestamisel on aluseks {POPULATION_VINTAGE} (
            {POPULATION_URL}).
          </InfoPopover>
        </p>
      }
    >
      {pending && <RankedSkeleton />}
      {!pending && rows.length === 0 && (
        <p className="mt-3 font-display text-stat leading-tight font-bold text-ink-muted">—</p>
      )}
      <ol className="mt-3 flex list-none flex-col gap-2 p-0">
        {rows.map((row) => {
          const unstated = row.key === UNSPECIFIED_COUNTY;
          return (
            <li
              key={row.key}
              className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-baseline gap-x-2"
            >
              <span
                className={`font-display text-[0.95rem] font-bold tabular-nums ${row.rank === 1 ? 'text-accent-deep' : 'text-line-strong'}`}
              >
                {row.rank ?? '–'}
              </span>
              <span
                className={`min-w-0 text-[0.85rem] break-words ${unstated ? 'text-ink-muted' : ''}`}
              >
                {countyLabel(row.key)}
              </span>
              <span
                className={`text-[0.85rem] font-semibold tabular-nums ${unstated ? 'text-ink-muted' : ''}`}
              >
                {mode === 'total' ? formatCount(row.value) : formatRate(row.value)}
              </span>
              <span className="col-start-2 col-end-4 mt-1 h-1.5 overflow-hidden rounded-[3px] bg-surface-sunken">
                {/* The unstated bucket is hatched, never coloured: it is kept in the picture
                    (FacetBar rule 3) without being drawn as a place that ranks. */}
                <span
                  className={`block h-full rounded-[3px] ${
                    unstated
                      ? 'bg-[repeating-linear-gradient(-45deg,var(--color-line-strong)_0_3px,transparent_3px_6px)]'
                      : row.rank === 1
                        ? 'bg-accent-deep'
                        : 'bg-accent'
                  }`}
                  style={{ width: `${(row.share * 100).toFixed(1)}%` }}
                />
              </span>
            </li>
          );
        })}
      </ol>
    </TablooCard>
  );
}

/** Kuu suurim kahju. A record plate is for property superlatives only — never deaths, never a
 * people-count (ui-direction §6) — and the framing says "stated", because that is all it is. */
export function RecordPlate({
  incident,
  yearBest,
  pending = false,
}: {
  incident: IncidentItem | null;
  yearBest: IncidentItem | null;
  /** Still fetching, as opposed to a month in which no report named a sum. */
  pending?: boolean;
}): JSX.Element {
  return (
    <TablooCard
      title="Kuu suurim kahju"
      caption={RECORD_DAMAGE_FRAMING}
      action={
        // The slot is reserved before the year's record lands, so the card does not grow a line
        // under the reader when it does.
        <div className="mt-3 min-h-[2.75rem]" data-slot="year-best">
          {yearBest?.damage_cents == null ? null : (
            <p className="border-t border-line pt-2 text-caption text-ink-muted">
              Aasta suurim: {formatEuros(yearBest.damage_cents)} (
              {formatDayMonth(yearBest.effective_date)}).
            </p>
          )}
        </div>
      }
    >
      {pending ? (
        <div className="mt-3 flex flex-col gap-2.5" aria-hidden="true">
          <Skeleton className="h-16 w-56" />
          <Skeleton className="h-4 w-32" />
        </div>
      ) : incident?.damage_cents == null ? (
        <p className="mt-3 text-[0.85rem] text-ink-muted">
          Ükski selle kuu teade ei nimetanud kahjusummat.
        </p>
      ) : (
        <>
          <span className="mt-3 inline-flex items-stretch overflow-hidden rounded-sm border-2 border-ink bg-surface shadow-card">
            <span
              aria-hidden="true"
              className="flex items-center bg-accent-deep px-2 text-[0.72rem] text-surface"
            >
              ★
            </span>
            <span className="px-3 pt-1.5 pb-2">
              <span className="block font-display text-[1.55rem] leading-[1.15] font-bold tracking-[-0.01em]">
                {formatEuros(incident.damage_cents)}
              </span>
              <span className="text-caption text-ink-secondary">
                {[
                  subcategoryLabel(incident.subcategory ?? '') ?? categoryLabel(incident.category),
                  incident.city,
                  formatDayMonth(incident.effective_date),
                ]
                  .filter((part) => part !== null && part !== '')
                  .join(' · ')}
              </span>
            </span>
          </span>
          <p className="mt-2.5 text-small">
            <a href={incident.post_url} target="_blank" rel="noreferrer" className="text-accent">
              Politsei teade ↗
            </a>
          </p>
        </>
      )}
    </TablooCard>
  );
}

/** Tüüpilised summad. The denominator sentence is the card's caption and is not optional: a median
 * over the reports that named a sum is not a median over thefts. */
export function MedianCards({
  theft,
  fraud,
}: {
  theft: number | null;
  fraud: number | null;
}): JSX.Element {
  return (
    <TablooCard
      title={
        <>
          Tüüpilised summad
          <InfoPopover label="Mida mediaan tähendab">
            Mediaan: pooled teadetes nimetatud summad jäävad sellest alla, pooled üle. Arvesse
            lähevad ainult need teated, mis summa nimetavad.
          </InfoPopover>
        </>
      }
      caption={MEDIAN_DENOMINATOR_NOTE}
    >
      <div className="mt-3 flex flex-wrap gap-6">
        {[
          { label: 'Tüüpiline vargus', value: theft },
          { label: 'Tüüpiline kelmus', value: fraud },
        ].map((median) => (
          <div key={median.label} className="flex flex-col">
            <span className="text-[0.78rem] font-semibold text-ink-secondary">{median.label}</span>
            <span className="font-display text-[1.75rem] leading-tight font-bold tracking-[-0.01em] tabular-nums">
              {median.value === null || median.value <= 0 ? '—' : formatEuros(median.value)}
            </span>
          </div>
        ))}
      </div>
    </TablooCard>
  );
}

const SPARK_WIDTH = 384;
const SPARK_BODY = 80;

/** Kellaajad. Twenty-four inline-SVG bars — no charting library reaches this page. */
export function HourSparkbar({
  hours,
  caption,
}: {
  hours: readonly FacetCount[];
  caption: string;
}): JSX.Element {
  const { bars, peak } = sparkbars(hours);
  const step = SPARK_WIDTH / 24;

  return (
    <TablooCard
      title="Kellaajad"
      caption={caption}
      // The caption gains the read span once the corpus bounds land, which is a second line.
      captionClassName="min-h-9"
      action={
        <p className="mt-3 text-small">
          <Link to="/liiklusonnetused#millal" className="text-accent">
            Millal õnnetused juhtuvad →
          </Link>
        </p>
      }
    >
      <div className="mt-3">
        <svg
          viewBox={`0 0 ${SPARK_WIDTH} 96`}
          role="img"
          aria-label={
            peak === null
              ? 'Liiklusõnnetuste jaotus kellaajati'
              : `Liiklusõnnetuste jaotus kellaajati, tipp kella ${peak.key} ajal`
          }
          className="block h-auto w-full"
        >
          <title>Liiklusõnnetused kellaajati</title>
          {bars.map((bar, i) => {
            const height = Math.max(1, bar.share * 62);
            return (
              <rect
                key={bar.key}
                x={i * step + 4}
                y={SPARK_BODY - height}
                width={8}
                height={height}
                rx={2}
                fill={bar.key === peak?.key ? 'var(--color-accent-deep)' : 'var(--chart-1)'}
              />
            );
          })}
          <line
            x1="0"
            y1={SPARK_BODY + 0.5}
            x2={SPARK_WIDTH}
            y2={SPARK_BODY + 0.5}
            stroke="var(--color-line-strong)"
          />
          {['00', '06', '12', '18', '23'].map((h) => (
            <text
              key={h}
              x={Number(h) * step + 8}
              y="92"
              textAnchor="middle"
              fontSize="9"
              fill="var(--color-ink-muted)"
            >
              {h}
            </text>
          ))}
          {peak !== null && (
            <text
              x={Math.min(SPARK_WIDTH - 20, Number(peak.key) * step + 8)}
              y="11"
              textAnchor="middle"
              fontSize="10"
              fontWeight="600"
              fill="var(--color-ink)"
            >
              {HOUR_PEAK_LABEL(peak.key)}
            </text>
          )}
        </svg>
      </div>
    </TablooCard>
  );
}
